import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ChatAPI } from '../services/api.js';
import { parseSSE } from '../services/stream.js';
import type { AuthMode, Channel, ChatMessage, ClientType } from '../types/chat.js';
import type { InlineInputType } from '../components/inline-input.js';

type InlinePrompt = {
  field: string;
  label: string;
  type: InlineInputType;
  placeholder: string;
  prefill: string;
};

type ConsentPrompt = {
  text: string;
  acceptLabel: string;
  declineLabel: string;
  tcUrl: string;
  privacyUrl: string;
  language: string;
};

type Identity = {
  email: string;
  firstName: string;
  otpVerified: boolean;
};

// AI transparency on the first interaction is contractual (client requirement 1,
// AI Act Reg. UE 2024/1689): the greeting states outright that this is an AI
// assistant AND carries the Terms + Privacy links the requirement asks for. This
// is now the single, deterministic place transparency is delivered on web, so
// the model is told NOT to repeat a transparency greeting (see system-prompt.md
// requirement 1). Links are markdown, rendered by the chat bubble.
const GREETINGS: Record<string, string> = {
  ro: 'Salut! Sunt asistentul virtual Urban Fitness, un asistent bazat pe inteligență artificială. Poți consulta [Termenii și Condițiile](https://urbanfitness.ro/termeni) și [Politica de Confidențialitate](https://urbanfitness.ro/confidentialitate). Te pot ajuta cu informații despre cluburi, abonamente, programe sau orice ai nevoie. Cu ce începem?',
  en: "Hi! I'm the Urban Fitness virtual assistant, powered by artificial intelligence. See our [Terms and Conditions](https://urbanfitness.ro/termeni) and [Privacy Policy](https://urbanfitness.ro/confidentialitate). I can help with club info, subscriptions, programs, or anything else you need. Where do we start?",
};

@customElement('urban-chat')
export class ChatWidget extends LitElement {
  static override styles = css`
    :host {
      --urban-primary: #0d0d0d;
      --urban-heading: #ffffff;
      --urban-text: #b0b0b0;
      --urban-muted: #7a7a7a;
      --urban-accent: #ff650c;
      --urban-accent-soft: rgba(255, 101, 12, 0.16);
      --urban-bg: #0f0f0f;
      --urban-bg-soft: #1a1a1a;
      --urban-border: #262626;

      position: fixed;
      bottom: 20px;
      right: 20px;
      width: 64px;
      height: 64px;
      font-family: 'Montserrat', system-ui, -apple-system, sans-serif;
      color: var(--urban-heading);
      z-index: 9999;
      transition: width 0.25s ease, height 0.25s ease;
    }
    :host([open]) {
      width: 420px;
      height: 680px;
      max-width: calc(100vw - 24px);
      max-height: calc(100vh - 40px);
    }
    .panel {
      width: 100%;
      height: 100%;
      background: var(--urban-bg);
      border-radius: 14px;
      box-shadow: 0 20px 50px rgba(16, 24, 40, 0.18);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .body {
      flex: 1;
      overflow-y: auto;
      padding: 14px 14px 16px;
      background: var(--urban-bg-soft);
      display: flex;
      flex-direction: column;
      overscroll-behavior: contain;
    }
    .body::-webkit-scrollbar { width: 6px; }
    .body::-webkit-scrollbar-thumb { background: var(--urban-muted); border-radius: 3px; }
    .error {
      color: #ff8a8a;
      font-size: 13px;
      padding: 8px 10px;
      background: rgba(220, 60, 60, 0.14);
      border: 1px solid rgba(220, 60, 60, 0.3);
      border-radius: 4px;
      margin: 6px 0;
    }
    .identity-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 6px 12px;
      background: var(--urban-bg-soft);
      border-bottom: 1px solid var(--urban-border);
      font-size: 12px;
      color: var(--urban-text);
    }
    .identity-banner .who {
      display: flex;
      align-items: center;
      gap: 6px;
      overflow: hidden;
    }
    .identity-banner .who .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--urban-accent);
      flex-shrink: 0;
    }
    .identity-banner .who span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .identity-banner .who strong {
      color: var(--urban-heading);
      font-weight: 600;
    }
    .identity-banner button {
      background: transparent;
      border: 1px solid var(--urban-border);
      color: var(--urban-text);
      padding: 3px 9px;
      border-radius: 6px;
      font: inherit;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    }
    .identity-banner button:hover {
      background: var(--urban-primary);
      color: white;
      border-color: var(--urban-primary);
    }
    .identity-banner button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .resend-row {
      display: flex;
      justify-content: flex-end;
      margin: 4px 0 8px;
    }
    .resend-row button {
      background: transparent;
      border: none;
      color: var(--urban-text);
      padding: 4px 8px;
      font: inherit;
      font-size: 12px;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
    }
    .resend-row button:hover { color: var(--urban-heading); }
    .resend-row button:disabled { opacity: 0.5; cursor: not-allowed; text-decoration: none; }
    .resend-row .feedback {
      font-size: 12px;
      color: var(--urban-text);
      padding: 4px 8px;
    }
    .fab {
      width: 64px;
      height: 64px;
      border-radius: 32px;
      background: var(--urban-accent);
      color: #0a0a0a;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
      transition: transform 0.2s ease;
      animation: fab-pulse 2.6s ease-in-out infinite;
      position: relative;
    }
    .fab:hover {
      transform: scale(1.08);
      animation: none;
    }
    .fab svg { width: 28px; height: 28px; }
    .fab .dot {
      position: absolute;
      top: 12px; right: 12px;
      width: 10px; height: 10px;
      border-radius: 50%;
      background: #ffffff;
      border: 2px solid #0a0a0a;
    }
    @keyframes fab-pulse {
      0%, 100% { box-shadow: 0 10px 30px rgba(0,0,0,0.45), 0 0 0 0 rgba(255,101,12,0.55); }
      50%      { box-shadow: 0 10px 30px rgba(0,0,0,0.45), 0 0 0 14px rgba(255,101,12,0); }
    }

    /* Mobile (max-width 600px): full-screen panel pinned to the VISUAL
       viewport (not the layout viewport). The host element's top/left/
       width/height are written by JS from window.visualViewport on every
       resize/scroll/orientationchange event - that is the only reliable
       way on iOS Safari to keep the chat input above the on-screen
       keyboard and to absorb the URL-bar showing/hiding.

       The CSS below provides the initial frame (used until the first JS
       tick attaches inline styles) and the visual chrome.

       Do NOT put backticks in this comment - they terminate the css
       template literal early and tsc explodes (lesson from PR #51). */
    @media (max-width: 600px) {
      :host([open]) {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: auto;
        height: auto;
        max-width: none;
        max-height: none;
      }
      .panel {
        border-radius: 0;
      }
      .body {
        padding-bottom: 12px;
      }
      urban-chat-input {
        padding-bottom: env(safe-area-inset-bottom);
      }
    }
  `;

  @property({ attribute: 'api-base' }) apiBase = '/api/v1';
  @property() channel: Channel = 'web';
  @property({ attribute: 'auth-mode' }) authMode: AuthMode = 'anonymous';
  @property() language = 'ro';
  @property() email = '';
  @property({ attribute: 'first-name' }) firstName = '';
  @property({ attribute: 'last-name' }) lastName = '';
  @property() phone = '';
  @property({ attribute: 'client-type' }) clientType: ClientType | '' = '';
  @property({ type: Boolean, reflect: true }) open = false;

  @state() private messages: ChatMessage[] = [];
  @state() private streaming = false;
  @state() private conversationId = '';
  @state() private error = '';
  @state() private inlinePrompt?: InlinePrompt;
  @state() private consentPrompt?: ConsentPrompt;
  @state() private identity?: Identity;
  @state() private resendStatus: 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error' = 'idle';

  private api?: ChatAPI;
  private abort?: AbortController;
  private streamingId = '';

  private buffer = '';
  private rafHandle = 0;
  private streamEnded = false;
  private charAccumulator = 0;
  private lastTickTime = 0;

  private vvHandler?: () => void;
  private bodyOverflowBackup = '';
  private bodyOverflowLocked = false;

  override connectedCallback() {
    super.connectedCallback();
    this.api = new ChatAPI(this.apiBase);
    this.addEventListener('demo-ask', this.handleDemoAsk as EventListener);
    void this.bootstrap();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('demo-ask', this.handleDemoAsk as EventListener);
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.abort?.abort();
    this.detachViewportTracking();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('open')) {
      if (this.open) {
        this.attachViewportTracking();
        queueMicrotask(() => {
          const body = this.renderRoot.querySelector('.body') as HTMLElement | null;
          if (body) body.scrollTo({ top: body.scrollHeight, behavior: 'instant' as ScrollBehavior });
        });
      } else {
        this.detachViewportTracking();
      }
    }
  }

  // Pin the host element to window.visualViewport on mobile so the chat
  // panel hugs the visible area, not the layout viewport. This is the
  // only reliable way on iOS Safari to keep the input above the soft
  // keyboard and to stay stable when the URL bar shows/hides. Above
  // 600px wide we leave the host alone (CSS handles the desktop FAB).
  private attachViewportTracking() {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      const isMobile = window.innerWidth <= 600;
      if (!isMobile) {
        this.style.removeProperty('top');
        this.style.removeProperty('left');
        this.style.removeProperty('width');
        this.style.removeProperty('height');
        this.unlockBodyScroll();
        return;
      }
      this.style.left = `${vv.offsetLeft}px`;
      this.style.top = `${vv.offsetTop}px`;
      this.style.width = `${vv.width}px`;
      this.style.height = `${vv.height}px`;
      this.lockBodyScroll();
    };
    this.vvHandler = apply;
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', apply);
    window.addEventListener('resize', apply);
    apply();
  }

  private detachViewportTracking() {
    const vv = window.visualViewport;
    if (this.vvHandler && vv) {
      vv.removeEventListener('resize', this.vvHandler);
      vv.removeEventListener('scroll', this.vvHandler);
      window.removeEventListener('orientationchange', this.vvHandler);
      window.removeEventListener('resize', this.vvHandler);
    }
    this.vvHandler = undefined;
    this.style.removeProperty('top');
    this.style.removeProperty('left');
    this.style.removeProperty('width');
    this.style.removeProperty('height');
    this.unlockBodyScroll();
  }

  private lockBodyScroll() {
    if (this.bodyOverflowLocked) return;
    this.bodyOverflowBackup = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.bodyOverflowLocked = true;
  }

  private unlockBodyScroll() {
    if (!this.bodyOverflowLocked) return;
    document.body.style.overflow = this.bodyOverflowBackup;
    this.bodyOverflowLocked = false;
  }

  private handleDemoAsk = (e: Event) => {
    const detail = (e as CustomEvent<{ text: string }>).detail;
    if (!detail?.text) return;
    this.open = true;
    queueMicrotask(() => this.send(detail.text));
  };

  private async bootstrap() {
    if (!this.api) return;
    try {
      const res = await this.api.start({
        channel: this.channel,
        authMode: this.authMode,
        language: this.language,
        email: this.email || undefined,
        firstName: this.firstName || undefined,
        lastName: this.lastName || undefined,
        phone: this.phone || undefined,
        clientType: this.clientType || undefined,
      });
      this.conversationId = res.conversationId;
      // auth_mode=automatic seeds OtpVerified at session start. Pull the
      // banner state immediately so the user sees their email pinned at
      // the top of the chat from the first frame.
      void this.refreshIdentity();
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private async send(text: string) {
    if (!this.api || !this.conversationId || this.streaming) return;
    this.error = '';
    this.appendUser(text);
    this.streamingId = this.appendAssistantPlaceholder();
    this.streaming = true;
    this.streamEnded = false;
    this.buffer = '';
    this.charAccumulator = 0;
    this.lastTickTime = 0;
    this.abort = new AbortController();
    try {
      const res = await this.api.streamMessage(this.conversationId, text, this.abort.signal);
      for await (const ev of parseSSE(res)) this.handleEvent(ev.type, ev.data);
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.streaming = false;
    }
  }

  private handleEvent(type: string, data: Record<string, unknown>) {
    if (type === 'text') this.pushDelta(data['delta'] as string);
    else if (type === 'tool_end') this.handleToolEnd(data);
    else if (type === 'error') this.error = (data['message'] as string) ?? 'error';
    else if (type === 'done') this.endStream();
  }

  private handleToolEnd(data: Record<string, unknown>) {
    const name = data['name'] as string;
    const result = data['result'] as Record<string, unknown> | undefined;
    if (!result) return;
    if (name === 'request_input' && result['form'] === 'input') {
      this.inlinePrompt = {
        field: (result['field'] as string) ?? 'generic',
        label: (result['label'] as string) ?? '',
        type: ((result['type'] as InlineInputType) ?? 'text'),
        placeholder: (result['placeholder'] as string) ?? '',
        prefill: (result['prefill'] as string) ?? '',
      };
      // Reset resend feedback whenever a fresh OTP input opens; lets the user
      // see new status text rather than a stale one carried over.
      if (this.inlinePrompt.field === 'otp') this.resendStatus = 'idle';
      this.scrollToBottom();
    }
    // GDPR consent card (Anexa 2 Sec. 3): rendered when the model calls
    // request_consent (or the server backstop injects it). Locks the
    // composer until the user decides.
    if (name === 'request_consent' && result['form'] === 'consent') {
      this.consentPrompt = {
        text: (result['text'] as string) ?? '',
        acceptLabel: (result['acceptLabel'] as string) ?? 'Sunt de acord',
        declineLabel: (result['declineLabel'] as string) ?? 'Nu acum',
        tcUrl: (result['tcUrl'] as string) ?? '',
        privacyUrl: (result['privacyUrl'] as string) ?? '',
        language: (result['language'] as string) ?? 'ro',
      };
      this.scrollToBottom();
    }
    // Identity transitions: refresh the banner whenever a tool flips session
    // identity state. We poll via /identity instead of reading the tool
    // result directly so the banner stays a single source of truth (the
    // backend session).
    if (name === 'verify_otp' || name === 'identify_member') {
      void this.refreshIdentity();
    }
  }

  /**
   * Consent decision handler. Order is load-bearing: the POST to /consent
   * must succeed BEFORE the follow-up chat message is sent - the audit row
   * and the server-side gate flag are written by that endpoint, and the
   * model's next turn relies on the gate being open. The visible chat
   * message keeps the conversation natural and transparent to the user.
   */
  private submitConsent = async (e: Event) => {
    const detail = (e as CustomEvent<{ decision: 'accepted' | 'declined' }>).detail;
    if (!detail?.decision || !this.api || !this.conversationId) return;
    const lang = this.consentPrompt?.language ?? 'ro';
    try {
      await this.api.consent(this.conversationId, detail.decision);
    } catch {
      this.error = lang === 'en'
        ? 'Could not record your choice. Please try again.'
        : 'Nu am putut înregistra alegerea ta. Te rog încearcă din nou.';
      this.consentPrompt = undefined;
      return;
    }
    this.consentPrompt = undefined;
    const followUp =
      detail.decision === 'accepted'
        ? lang === 'en' ? 'I agree to the terms.' : 'Sunt de acord cu termenii.'
        : lang === 'en' ? 'Not now.' : 'Nu acum.';
    queueMicrotask(() => this.send(followUp));
  };

  private async refreshIdentity() {
    if (!this.api || !this.conversationId) return;
    try {
      const snap = await this.api.identity(this.conversationId);
      if (snap.email && snap.otpVerified) {
        this.identity = {
          email: snap.email,
          firstName: snap.firstName ?? '',
          otpVerified: true,
        };
      } else {
        this.identity = undefined;
      }
    } catch {
      // Best-effort. Banner stays in its previous state.
    }
  }

  private logout = async () => {
    if (!this.api || !this.conversationId) return;
    try {
      await this.api.logout(this.conversationId);
    } catch {
      // Even on network failure we clear local state — backend session
      // either expired or doesn't exist; either way the user wanted out.
    }
    this.identity = undefined;
  };

  private resendOTP = async () => {
    if (!this.api || !this.conversationId || this.resendStatus === 'sending') return;
    this.resendStatus = 'sending';
    try {
      const out = await this.api.resendOTP(this.conversationId);
      if (out.sent) this.resendStatus = 'sent';
      else if (out.rateLimited) this.resendStatus = 'rate-limited';
      else if (out.alreadyVerified) this.resendStatus = 'sent';
      else this.resendStatus = 'error';
    } catch {
      this.resendStatus = 'error';
    }
  };

  private submitInline = (e: Event) => {
    const detail = (e as CustomEvent<{ field: string; label: string; value: string }>).detail;
    if (!detail?.value) return;
    const label = detail.label || detail.field;
    this.inlinePrompt = undefined;
    queueMicrotask(() => this.send(`${label}: ${detail.value}`));
  };

  private pushDelta(delta: string) {
    if (!delta) return;
    this.buffer += delta;
    if (!this.rafHandle) this.scheduleTick();
  }

  private scheduleTick() {
    this.rafHandle = requestAnimationFrame((t) => this.tick(t));
  }

  private tick(now: number) {
    this.rafHandle = 0;
    if (this.buffer.length === 0) {
      this.charAccumulator = 0;
      this.lastTickTime = 0;
      if (this.streamEnded) this.finalize();
      return;
    }

    const dt = this.lastTickTime ? Math.min((now - this.lastTickTime) / 1000, 0.1) : 0.016;
    this.lastTickTime = now;

    const pending = this.buffer.length;
    let cps = 28;
    if (pending > 60) cps = 55;
    if (pending > 180) cps = 110;
    if (pending > 450) cps = pending / 2;

    this.charAccumulator += cps * dt;
    const take = Math.min(Math.floor(this.charAccumulator), this.buffer.length);
    this.charAccumulator -= take;

    if (take > 0) {
      const chunk = this.buffer.slice(0, take);
      this.buffer = this.buffer.slice(take);
      this.messages = this.messages.map((m) =>
        m.id === this.streamingId ? { ...m, content: m.content + chunk } : m,
      );
      this.scrollToBottomIfPinned();
    }

    if (this.buffer.length > 0 || !this.streamEnded) {
      this.scheduleTick();
    } else {
      this.finalize();
    }
  }

  private endStream() {
    this.streamEnded = true;
    if (!this.rafHandle && this.buffer.length === 0) {
      this.finalize();
    }
  }

  private finalize() {
    const id = this.streamingId;
    this.messages = this.messages.map((m) => (m.id === id ? { ...m, pending: false } : m));
  }

  private appendUser(content: string) {
    this.messages = [...this.messages, { id: crypto.randomUUID(), role: 'user', content }];
    this.scrollToBottom();
  }

  private appendAssistantPlaceholder(): string {
    const id = crypto.randomUUID();
    this.messages = [...this.messages, { id, role: 'assistant', content: '', pending: true }];
    this.scrollToBottom();
    return id;
  }

  private scrollToBottomIfPinned() {
    // Streaming deltas: snap to bottom instantly so text never lags behind
    // the viewport. Smooth animation can't keep up at ~28-110 chars/sec.
    queueMicrotask(() => {
      const body = this.renderRoot.querySelector('.body') as HTMLElement | null;
      if (!body) return;
      const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
      if (distanceFromBottom < 200) {
        body.scrollTo({ top: body.scrollHeight, behavior: 'auto' });
      }
    });
  }

  private scrollToBottom() {
    // New discrete content (bubble, inline-input). Use rAF so layout has
    // settled before measuring scrollHeight, then animate smoothly.
    requestAnimationFrame(() => {
      const body = this.renderRoot.querySelector('.body') as HTMLElement | null;
      if (body) body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    });
  }

  private toggle = () => { this.open = !this.open; };

  private reset = async () => {
    this.abort?.abort();
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = 0;
    this.messages = [];
    this.streaming = false;
    this.streamEnded = false;
    this.buffer = '';
    this.charAccumulator = 0;
    this.lastTickTime = 0;
    this.error = '';
    this.inlinePrompt = undefined;
    this.identity = undefined;
    this.resendStatus = 'idle';
    this.conversationId = '';
    await this.bootstrap();
  };

  private greeting() {
    return GREETINGS[this.language] ?? GREETINGS['ro'];
  }

  override render() {
    return this.open ? this.renderPanel() : this.renderFab();
  }

  private renderFab() {
    return html`
      <button class="fab" @click=${this.toggle} aria-label="Deschide chat">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 3C6.48 3 2 6.94 2 11.8c0 2.07.83 3.97 2.21 5.49.05.91-.24 2.59-1.43 3.71-.06.06-.08.15-.05.23.03.08.11.13.19.13 1.95 0 3.87-.69 5.13-1.36 1.21.4 2.55.6 3.95.6 5.52 0 10-3.94 10-8.8C22 6.94 17.52 3 12 3z"/>
        </svg>
        <span class="dot"></span>
      </button>
    `;
  }

  private renderPanel() {
    return html`
      <div class="panel">
        <urban-chat-header
          subtitle=${this.language === 'en' ? 'AI virtual assistant' : 'Asistent virtual AI'}
          @close=${this.toggle}
          @reset=${this.reset}
        ></urban-chat-header>
        ${this.identity
          ? html`<div class="identity-banner" role="status" aria-live="polite">
              <div class="who">
                <span class="dot"></span>
                <span>Conectat ca <strong>${this.identity.email}</strong></span>
              </div>
              <button type="button" @click=${this.logout} title="Schimbă utilizatorul / logout">
                Logout
              </button>
            </div>`
          : ''}
        <div class="body">
          ${this.messages.length === 0
            ? html`<urban-chat-bubble role="assistant" content=${this.greeting()}></urban-chat-bubble>`
            : ''}
          ${this.messages
            .filter((m) => !(m.role === 'assistant' && m.pending && m.content === ''))
            .map(
              (m) => html`<urban-chat-bubble
                role=${m.role}
                content=${m.content}
                ?pending=${m.pending}
              ></urban-chat-bubble>`,
            )}
          ${this.streaming && this.lastMessageEmpty()
            ? html`<urban-typing-indicator></urban-typing-indicator>`
            : ''}
          ${this.inlinePrompt
            ? html`<urban-inline-input
                field=${this.inlinePrompt.field}
                label=${this.inlinePrompt.label}
                type=${this.inlinePrompt.type}
                placeholder=${this.inlinePrompt.placeholder}
                prefill=${this.inlinePrompt.prefill}
                @inline-submit=${this.submitInline}
              ></urban-inline-input>`
            : ''}
          ${this.inlinePrompt?.field === 'otp' ? this.renderResendRow() : ''}
          ${this.consentPrompt
            ? html`<urban-consent-card
                text=${this.consentPrompt.text}
                acceptLabel=${this.consentPrompt.acceptLabel}
                declineLabel=${this.consentPrompt.declineLabel}
                tcUrl=${this.consentPrompt.tcUrl}
                privacyUrl=${this.consentPrompt.privacyUrl}
                language=${this.consentPrompt.language}
                @consent-decision=${this.submitConsent}
              ></urban-consent-card>`
            : ''}
          ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        </div>
        <urban-chat-input
          ?disabled=${this.streaming || !this.conversationId || !!this.inlinePrompt || !!this.consentPrompt}
          placeholder=${this.inlinePrompt || this.consentPrompt ? 'Răspunde mai sus…' : 'Scrie un mesaj…'}
          @send=${(e: CustomEvent) => this.send(e.detail.text)}
        ></urban-chat-input>
      </div>
    `;
  }

  private lastMessageEmpty(): boolean {
    const last = this.messages[this.messages.length - 1];
    return !!last && last.role === 'assistant' && last.content === '';
  }

  private renderResendRow() {
    const label = (() => {
      switch (this.resendStatus) {
        case 'sending': return 'Se trimite...';
        case 'sent': return 'Cod nou trimis. Verifică emailul.';
        case 'rate-limited': return 'Așteaptă câteva minute înainte să mai ceri un cod.';
        case 'error': return 'Nu am putut trimite codul. Încearcă din nou.';
        default: return '';
      }
    })();
    return html`
      <div class="resend-row">
        ${label
          ? html`<span class="feedback">${label}</span>`
          : html`<button
              type="button"
              ?disabled=${this.resendStatus === 'sending'}
              @click=${this.resendOTP}
            >Nu ai primit codul?</button>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'urban-chat': ChatWidget;
  }
}
